// Same localStorage key the old zustand/devices-store.js used for a bare
// deviceId string — kept unchanged (only the payload shape changes) so
// movexStudio's unrelated logout cleanup (a plain
// localStorage.removeItem('camera-device')) needs no update.
const STORAGE_KEY = 'camera-device';

function safeParseJson(value) {
  try {
    return JSON.parse(value);
  } catch (_error) {
    return null;
  }
}

/**
 * Reads `{ deviceId, facingMode, width, height, timestamp }`, or `null` if
 * nothing usable is stored — never the number `0` the old devices-store.js
 * fell back to, which a truthiness check could mistake for "no preference".
 * A legacy bare-string value (written by that old store) has no `.deviceId`
 * and is treated the same as absent, not as a crash.
 */
export function readCameraPreference() {
  let raw;
  try {
    raw = localStorage.getItem(STORAGE_KEY);
  } catch (_error) {
    return null;
  }

  if (!raw) {
    return null;
  }

  const parsed = safeParseJson(raw);
  if (!parsed || typeof parsed !== 'object' || !parsed.deviceId) {
    return null;
  }

  return {
    deviceId: parsed.deviceId,
    facingMode: parsed.facingMode ?? null,
    width: parsed.width ?? null,
    height: parsed.height ?? null,
    timestamp: parsed.timestamp ?? null,
  };
}

/**
 * Called automatically by the store every time a stream is successfully
 * applied — callers never need to remember to persist separately.
 */
export function writeCameraPreference({ deviceId, facingMode, width, height }) {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        deviceId: deviceId ?? null,
        facingMode: facingMode ?? null,
        width: width ?? null,
        height: height ?? null,
        timestamp: Date.now(),
      })
    );
  } catch (_error) {
    // Storage can throw in a hardened/incognito context — persistence is a
    // nice-to-have, never a reason to fail the acquisition that triggered it.
  }
}
